/**
 * Русские подписи статусов и enum-значений домена — одно место для бейджей и фильтров.
 * Значения ключей — как их отдаёт бэкенд (см. сущности в backend/src/modules/**).
 * Неизвестное значение показывается как есть через labelOf(), чтобы не терять данные.
 */

/** Роль пользователя (User.role). */
export const USER_ROLE_LABELS: Record<string, string> = {
  user: 'Пользователь',
  admin: 'Админ',
};

/** Заказ — backend/src/modules/payments/entities/order.entity.ts (OrderStatus). */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает оплаты',
  paid: 'Оплачен',
  cancelled: 'Отменён',
  refunded: 'Возврат',
};

/** Платёж — backend/src/modules/payments/entities/payment.entity.ts (PaymentStatus). */
export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  initiated: 'Создан',
  success: 'Успешно',
  failed: 'Ошибка',
  expired: 'Истёк',
  cancelled: 'Отменён (возврат)',
};

/** Платёжные провайдеры — названия брендов не переводятся. */
export const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  click: 'Click',
  payme: 'Payme',
  paylov: 'Paylov',
};

/** Подписка — backend/src/modules/payments/entities/subscription.entity.ts (SubscriptionStatus). */
export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  active: 'Активна',
  expired: 'Истекла',
  cancelled: 'Отменена',
};

/** Push-рассылки — admin/src/api/notifications.ts (NotificationStatus). */
export const NOTIFICATION_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  scheduled: 'Запланировано',
  sent: 'Отправлено',
  failed: 'Ошибка',
  cancelled: 'Отменено',
};

/** Тип уведомления — admin/src/api/notifications.ts (NotificationType). */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  system: 'Система',
  payment: 'Платёж',
  subscription: 'Подписка',
  promo: 'Промо',
  reminder: 'Напоминание',
  achievement: 'Достижение',
};

/** Вид экзамена — admin/src/api/learner.ts (ExamKind). */
export const EXAM_KIND_LABELS: Record<string, string> = {
  mock: 'Пробный',
  retake: 'Пересдача',
  wrong: 'Работа над ошибками',
  marathon: 'Марафон',
  ticket: 'Билет',
  topic: 'По теме',
};

/** Статус матча дуэли — backend/src/modules/games/entities/duel-match.entity.ts (DuelStatus). */
export const DUEL_STATUS_LABELS: Record<string, string> = {
  waiting: 'Ожидает соперника',
  active: 'Идёт',
  completed: 'Завершён',
  expired: 'Истёк',
};

/** Тип видеоурока. */
export const VIDEO_LESSON_TYPE_LABELS: Record<string, string> = {
  practical: 'Практика',
  theoritical: 'Теория',
  other: 'Другое',
};

/** События безопасности — backend/src/modules/security/entities/security-audit-log.entity.ts. */
export const SECURITY_EVENT_LABELS: Record<string, string> = {
  LOGIN_SUCCESS: 'Успешный вход',
  LOGIN_FAILED: 'Неудачный вход',
  LOGOUT: 'Выход',
  SESSION_CREATED: 'Новая сессия',
  SESSION_REVOKED: 'Сессия отозвана',
  DEVICE_LIMIT_EXCEEDED: 'Превышен лимит устройств',
  GEO_ANOMALY: 'Гео-аномалия',
  RAPID_LOCATION_CHANGE: 'Быстрая смена местоположения',
  SUSPICIOUS_IP: 'Подозрительный IP',
  RATE_LIMIT_EXCEEDED: 'Слишком много запросов',
  TOKEN_REUSE: 'Повторное использование токена',
  CONCURRENT_SESSIONS: 'Одновременные сессии',
  ACCOUNT_SHARING_SUSPECTED: 'Подозрение на шеринг аккаунта',
  TIME_MANIPULATION: 'Подмена времени',
  DEVICE_MISMATCH: 'Несовпадение устройства',
  DUPLICATE_MERGED: 'Дубль склеен',
};

/** Уровень события безопасности (SecuritySeverity). */
export const SECURITY_SEVERITY_LABELS: Record<string, string> = {
  INFO: 'Инфо',
  WARNING: 'Предупреждение',
  CRITICAL: 'Критично',
};

/**
 * Периоды активности на дашборде. Бэкенд отдаёт английские `name`
 * (backend/src/modules/users/users.service.ts, getActiveUsersByPeriodComparison).
 */
export const ACTIVITY_PERIOD_LABELS: Record<string, string> = {
  'Daily Active Users': 'День (DAU)',
  'Weekly Active Users': 'Неделя (WAU)',
  'Monthly Active Users': 'Месяц (MAU)',
};

/** Подпись значения по словарю; неизвестное значение возвращается как есть, пустое — «—». */
export function labelOf(map: Record<string, string>, value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return map[value] ?? value;
}
